import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { canBeAssigned } from '@/features/access/signIn';
import { useI18n } from '@/i18n';
import { api, ApiError } from '@/lib/api';
import { dateShort, dateTime, duration, fileSize, fullName, time } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';
import { Combobox } from '@/components/Combobox';
import { personOptions } from '@/features/access/people';

/** Mirrors the server-side state machine. The API rejects anything else anyway. */
const TRANSITIONS: Record<string, string[]> = {
  draft: ['scheduled', 'cancelled'],
  scheduled: ['in_progress', 'on_hold', 'cancelled'],
  in_progress: ['on_hold', 'completed', 'cancelled'],
  on_hold: ['in_progress', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function JobDetailPage() {
  const { t } = useI18n();
  const L = useLabel();
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [assignee, setAssignee] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');

  const job = useQuery({ queryKey: ['job', id], queryFn: () => api.job(id) });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const documents = useQuery({
    queryKey: ['documents', { jobId: id }],
    queryFn: () => api.documents({ jobId: id }),
  });
  const notes = useQuery({ queryKey: ['notes', id], queryFn: () => api.jobNotes(id) });
  const shifts = useQuery({
    queryKey: ['shifts', { jobId: id }],
    queryFn: () => api.shifts({ jobId: id, limit: 50 }),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['job', id] });
    await queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };

  const assign = useMutation({
    mutationFn: (userId: string) => api.assignJob(id, [userId]),
    onSuccess: async () => {
      setAssignee('');
      await invalidate();
    },
  });

  const unassign = useMutation({
    mutationFn: (userId: string) => api.unassignJob(id, userId),
    onSuccess: invalidate,
  });

  const changeStatus = useMutation({
    mutationFn: () => api.setJobStatus(id, pendingStatus, statusReason.trim() || undefined),
    onSuccess: async () => {
      setPendingStatus('');
      setStatusReason('');
      await invalidate();
    },
  });

  if (job.isLoading) return <Spinner />;
  if (job.error) return <ErrorNotice error={job.error} />;
  if (!job.data) return null;

  const j = job.data.job;
  const status = L('job', j.status);
  const allUsers = users.data?.users ?? [];
  const assigned = allUsers.filter((u) => j.assignedUserIds.includes(u.id));
  const available = allUsers.filter(
    (u) => !j.assignedUserIds.includes(u.id) && canBeAssigned(u),
  );

  // Reasons are mandatory for the two transitions someone will ask about later.
  const needsReason = pendingStatus === 'on_hold' || pendingStatus === 'cancelled';

  return (
    <>
      <PageHeader
        title={j.title}
        subtitle={`${j.reference}${j.client?.name ? ` · ${j.client.name}` : ''}`}
        actions={
          <>
            <Link to="/jobs" className="btn-secondary">
              {t('common.backToJobs')}
            </Link>
            <Badge tone={status.tone}>{status.label}</Badge>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {j.description ? (
            <Card>
              <h2 className="font-bold mb-2">{t('common.description')}</h2>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{j.description}</p>
            </Card>
          ) : null}

          <Card>
            <h2 className="font-bold mb-3">{t('common.site')}</h2>
            {j.site ? (
              <>
                <p className="font-semibold text-sm">{j.site.name}</p>
                <p className="text-sm text-muted">
                  {j.site.addressLine1}
                  {j.site.city ? `, ${j.site.postcode ?? ''} ${j.site.city}` : ''}
                </p>
                {j.site.geofenceRadiusM !== null ? (
                  <p className="text-sm text-muted mt-2 tabular">
                    {t('jobs.siteArea', { radius: j.site.geofenceRadiusM })}
                    {j.site.lat !== null && j.site.lng !== null
                      ? ` · ${j.site.lat.toFixed(5)}, ${j.site.lng.toFixed(5)}`
                      : ''}
                  </p>
                ) : null}
                {j.site.accessNotes ? (
                  <div className="mt-3 p-3 rounded bg-[var(--color-primary-soft)]">
                    <div className="label-text text-primary mb-1">{t('jobs.access')}</div>
                    <p className="text-sm">{j.site.accessNotes}</p>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted">{t('common.noSiteLinked')}</p>
            )}
          </Card>

          <Card padded={false}>
            <h2 className="font-bold p-4 pb-3">{t('jobs.shiftsOnJob')}</h2>
            <div className="table-scroll">
              <table>
                <tbody>
                  {(shifts.data?.shifts ?? []).map((s) => {
                    const user = allUsers.find((u) => u.id === s.userId);
                    return (
                      <tr key={s.id} className="hover:bg-surface-alt">
                        <td className="td font-semibold">{user ? fullName(user) : '—'}</td>
                        <td className="td tabular text-muted whitespace-nowrap">
                          {dateShort(s.clockInAt)} · {time(s.clockInAt)}
                          {s.clockOutAt ? `–${time(s.clockOutAt)}` : ''}
                        </td>
                        <td className="td tabular font-semibold">{duration(s.durationMinutes)}</td>
                        <td className="td text-end">
                          <Link to={`/shifts/${s.id}`} className="td-action text-primary hover:underline">
                            {t('common.details')}
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(shifts.data?.shifts ?? []).length === 0 ? (
              <div className="px-4 pb-6 text-sm text-muted">{t('common.noShiftsRecorded')}</div>
            ) : null}
          </Card>

          <Card padded={false}>
            <h2 className="font-bold p-4 pb-3">{t('documents.title')}</h2>
            <div className="table-scroll">
              <table>
                <tbody>
                  {(documents.data?.documents ?? []).map((d) => {
                    const cat = L('docCategory', d.category);
                    return (
                      <tr key={d.id} className="hover:bg-surface-alt">
                        <td className="td font-semibold">{d.originalFilename}</td>
                        <td className="td">
                          <Badge tone={cat.tone}>{cat.label}</Badge>
                        </td>
                        <td className="td text-muted tabular whitespace-nowrap">
                          {fileSize(d.sizeBytes)}
                        </td>
                        <td className="td text-muted text-sm whitespace-nowrap">
                          {dateShort(d.uploadedAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(documents.data?.documents ?? []).length === 0 ? (
              <div className="px-4 pb-6 text-sm text-muted">{t('common.noDocumentsAttached')}</div>
            ) : null}
          </Card>

          {(notes.data?.notes ?? []).length > 0 ? (
            <Card>
              <h2 className="font-bold mb-3">{t('jobs.siteNotes')}</h2>
              <ul className="space-y-3">
                {(notes.data?.notes ?? []).map((n) => {
                  const note = n as { id: string; body: string; author_name: string; created_at: string };
                  return (
                    <li key={note.id} className="border-b border-border last:border-0 pb-3 last:pb-0">
                      <p className="text-sm">{note.body}</p>
                      <p className="text-xs text-subtle mt-1">
                        {note.author_name} · {dateTime(note.created_at)}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ) : null}
        </div>

        <div className="space-y-6">
          <Card>
            <h2 className="font-bold mb-3">{t('jobs.team')}</h2>
            {assigned.length === 0 ? (
              <p className="text-sm text-warning font-semibold mb-3">
                {t('jobs.noTeamWarning')}
              </p>
            ) : (
              <ul className="space-y-2 mb-3">
                {assigned.map((u) => (
                  <li key={u.id} className="flex items-center gap-2">
                    <span className="flex-1 text-sm font-medium">{fullName(u)}</span>
                    <button
                      type="button"
                      className="text-xs text-danger font-semibold hover:underline"
                      onClick={() => unassign.mutate(u.id)}
                    >
                      {t('common.remove')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex gap-2">
              <Combobox
                className="flex-1"
                value={assignee}
                onChange={setAssignee}
                placeholder={t('common.add')}
                options={personOptions(available, L)}
              />
              <button
                type="button"
                className="btn-primary"
                disabled={!assignee || assign.isPending}
                onClick={() => assign.mutate(assignee)}
              >
                {t('common.assign')}
              </button>
            </div>
          </Card>

          <Card>
            <h2 className="font-bold mb-3">{t('jobs.scheduling')}</h2>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-4">
                <dt className="text-muted">{t('common.start')}</dt>
                <dd className="tabular font-medium">
                  {j.scheduledStart ? dateTime(j.scheduledStart) : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted">{t('common.end')}</dt>
                <dd className="tabular font-medium">
                  {j.scheduledEnd ? dateTime(j.scheduledEnd) : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted">{t('jobs.signatureRequired')}</dt>
                <dd className="font-medium">{t(j.requiresSignature ? 'common.yes' : 'common.no')}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h2 className="font-bold mb-3">{t('jobs.changeStatus')}</h2>
            {(TRANSITIONS[j.status] ?? []).length === 0 ? (
              <p className="text-sm text-muted">
                {t('jobs.finalState')}
              </p>
            ) : (
              <div className="space-y-3">
                <select
                  className="input"
                  value={pendingStatus}
                  onChange={(e) => setPendingStatus(e.target.value)}
                >
                  <option value="">{t('common.select')}</option>
                  {(TRANSITIONS[j.status] ?? []).map((s) => (
                    <option key={s} value={s}>
                      {L('job', s).label}
                    </option>
                  ))}
                </select>

                {needsReason ? (
                  <Field label={t('common.reasonRequired')}>
                    <textarea
                      className="input h-20 py-2 resize-none"
                      value={statusReason}
                      onChange={(e) => setStatusReason(e.target.value)}
                    />
                  </Field>
                ) : null}

                {changeStatus.error ? (
                  <p className="text-sm text-danger">
                    {changeStatus.error instanceof ApiError
                      ? changeStatus.error.message
                      : t('jobs.statusChangeFailed')}
                  </p>
                ) : null}

                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={
                    !pendingStatus ||
                    (needsReason && !statusReason.trim()) ||
                    changeStatus.isPending
                  }
                  onClick={() => changeStatus.mutate()}
                >
                  {changeStatus.isPending ? t('common.saving') : t('common.apply')}
                </button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
